import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Clock, PlayCircle } from 'lucide-react';
import type { Serie } from '@/types/masterclass';
import { useMasterClassProgress } from '@/hooks/useMasterClassProgress';

interface MasterClassCardProps {
  serie: Serie;
  onOpenSerie?: (serie: Serie) => void;
}

export function MasterClassCard({ serie, onOpenSerie }: MasterClassCardProps) {
  useLanguage();
  const { progressoPct } = useMasterClassProgress(serie.id);
  
  const handleClick = () => {
    onOpenSerie?.(serie);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };
  
  return (
    <div 
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="cursor-pointer block space-y-3"
    >
      {/* Thumbnail */}
      <div 
        className="relative aspect-video overflow-hidden bg-muted/30 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg" 
        style={{ 
          borderRadius: 'var(--radius-md)', 
          border: '1px solid var(--gray-200)' 
        }}
      >
        {/* Área de preview com ícone de play */}
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center transition-colors">
            <PlayCircle className="w-10 h-10 text-white/60 transition-colors" />
          </div>
        </div>
        
        {/* Badge de duração */}
        <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm rounded-md px-2 py-1 flex items-center gap-1">
          <Clock className="w-3 h-3 text-white/80" />
          <span className="text-xs text-white/80 font-medium">{serie.duracaoTotalMin}{translateInterfaceText("min")}</span>
        </div>
        
        {/* Progress bar */}
        {progressoPct > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/30">
            <div 
              className="h-full bg-primary transition-all duration-300" 
              style={{ width: `${progressoPct}%` }}
            />
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="space-y-1.5">
        {/* Título */}
        <h3 className="font-semibold text-base leading-tight line-clamp-2">
          {serie.titulo}
        </h3>
        
        {/* Descrição */}
        <p className="text-sm text-muted-foreground line-clamp-2">
          {serie.subtitulo}
        </p>
      </div>
    </div>
  );
}
